<template>
  <div>
    <alert ref="alert"/>
    <manage-portfolios-modal
        ref="managePortfoliosModal"
        :portfolios="portfolios"
        :unsaved="unsavedChanges"
        @addPortfolio="onPortfolioAdded"
        @loadPortfolio="onPortfolioLoaded"
        @deletePortfolio="onPortfolioDeleted"
        @error="onError"/>

    <save-as-modal ref="saveAsModal" @saveAs="saveAs"/>

    <div v-if="showSpinner" class="loader-wrapper">
      <div class="loader"></div>
    </div>
    <b-row class="mt-2 mb-2">
      <b-col cols="3">
        <b-form-group label="Portfolio" label-for="portfolio_select" class="text-left" label-class="font-weight-bold">
          <b-form-select id="portfolio_select"
                         v-bind:value="selectedPortfolio"
                         @change="changeSelectedPortfolio"
                         :disabled="portfolios.length == 0 || (portfolios[selectedPortfolio] && isEditable && !isNew)">
            <option v-for="portfolio of portfolios" :key="portfolio.Name" :value="portfolio.Name">
              {{ portfolio.hasOwnProperty("IsNew") ? 'NEW ' : '' }} {{ portfolio.Name }}
            </option>
          </b-form-select>
        </b-form-group>
        <div class="text-left mb-2">Type: {{ portfolioType }}</div>
      </b-col>
      <b-col cols="1" style="min-width: 140px">
        <b-form-group label="Settlement Date" label-for="settlement_date" class="text-left text-nowrap" label-class="font-weight-bold" style="min-width:13ch">
          <datepicker id="settlement_date" :clearable="false" class="w-100" input-class="form-control text-right calculation-settings-input"
                      type="date" format="MM/DD/YYYY" lang="en" v-model="settlementDate"/>
        </b-form-group>
      </b-col>
      <b-col cols="1" v-if='portfolioType == "Reinsurance"' style="min-width: 140px">
        <b-form-group label="Interest Rate (%)" label-for="settlement_date" class="text-left" label-class="font-weight-bold parameters-label-condensed">
          <b-form-input v-model="interestRate" class="calculation-settings-input text-right"/>
        </b-form-group>
      </b-col>
      <b-col cols="1" v-else>
        <b-form-group label="Index (%)" label-for="settlement_date" class="text-left" label-class="font-weight-bold">
          <b-form-input v-model="libor" class="calculation-settings-input text-right"/>
        </b-form-group>
      </b-col>
      <b-col cols="2">
        <b-form-group label=" " class="text-left">
          <b-btn variant="primary" @click="runPortfolio" :disabled="!isPortfolioConsistent">Analyze portfolio</b-btn>
        </b-form-group>
      </b-col>
      <b-col cols="auto" class="text-right">
        <b-form-group label=" " class="text-left">
          <b-btn variant="info" @click="showModal(1)" class="text-nowrap mr-1">Manage portfolios</b-btn>
          <b-btn variant="info" @click="save" class="text-nowrap mr-1">Save</b-btn>
          <b-btn variant="info" @click="showModal(2)" class="text-nowrap mr-1">Save As</b-btn>
          <b-btn variant="info" @click="exportPortfolioToExcel" class="text-nowrap mr-1">Export to XLSX</b-btn>
          <b-btn variant="outline-info" @click="reload" class="text-nowrap mr-1">Reload</b-btn>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col class="text-left">
        <b-btn variant="primary" size="sm" @click="toggleEditable()" v-if="!isNew">
          {{ portfolios[selectedPortfolio] && isEditable ? 'Cancel' : 'Edit' }}
        </b-btn>
        <b-btn variant="info" size="sm" @click="addPortfolioEntry" v-if="portfolios[selectedPortfolio] && isEditable">
          Add portfolio entry
        </b-btn>
        <b-btn variant="primary" size="sm" @click="updatePortfolio()" v-if="portfolios[selectedPortfolio] && isEditable && !isNew">Update</b-btn>
      </b-col>
    </b-row>
    <div class="table-relative">
    <table class="table table-sm color mb-2" v-if="isPortfolioConsistent">
      <thead>
      <tr>
        <th colspan="2"></th>
        <!-- Deal Name -->
        <th>Deal Name</th>
        <!-- Bond Name -->
        <th>Bond Name</th>
        <!-- Initial Limit / Balance -->
        <th class="text-right">
          <span>{{ portfolioType == 'Reinsurance' ? 'Initial Limit ($)' : 'Balance ($)' }}</span>
        </th>
        <!-- Current Limit / Current Balance -->
        <th class="text-right">
          <span>{{ portfolioType == 'Reinsurance' ? 'Current Limit ($)' : 'Current Balance ($)' }}</span>
        </th>
        <!-- Collateral / Price -->
        <th class="text-right">
          <span>{{ portfolioType == 'Reinsurance' ? 'Collateral (%)' : 'Price ($)' }}</span>
        </th>
        <!-- CPR -->
        <th>CPR (%)</th>
        <!-- CDR -->
        <th>CDR (%)</th>
        <!-- SEV -->
        <th>SEV (%)</th>
        <!-- DLQ -->
        <th>DLQ (%)</th>
        <!-- Scenario -->
        <th>Scenario</th>
        <!-- Call -->
        <th>
          <b-form-group :label="portfolioType == 'Reinsurance' ? 'Early Call Option ' : 'Cleanup Call'"
                        label-for="call_checkbox"
                        class="m-0 p-0 align-bottom">
            <b-form-checkbox id="call_checkbox" v-model="earlyCall" class="transformed"/>
          </b-form-group>
        </th>
        <!-- ROI / Yield -->
        <th class="text-right">
          <span>{{ portfolioType == 'Reinsurance' ? 'ROI' : 'Yield' }}</span>
        </th>
        <!-- Rate On Line / WAL -->
        <th class="text-right">
          <span>{{ portfolioType == 'Reinsurance' ? 'Rate On Line' : 'WAL' }}</span>
        </th>
        <!-- Loss On Line / Write down -->
        <th class="text-right">
          <span>{{ portfolioType == 'Reinsurance' ? 'Loss On Line' : 'Write down' }}</span>
        </th>
        <!--Margin On Line / Spread -->
        <th class="text-right">
          <span>{{ portfolioType == 'Reinsurance' ? 'Margin On Line' : 'Spread' }}</span>
        </th>
      </tr>
      </thead>
      <tbody>
      <portfolio-entry
          v-for="(portfolioEntry, index) in portfolios[selectedPortfolio].PortfolioEntries"
          :key="index"
          :entry="portfolioEntry"
          :index="index"
          :deals="applicableDeals"
          :vectors="vectors"
          :editable="isEditable"
          @dealNameChanged="onDealNameChange"
          @removeEntry="removeEntry"
          @portfolioChanged="portfolioChanged"
          @CPRVectorChange="portfolioChanged"
          @onChildChanged="onChildChanged"
      />
      <tr v-if="portfolioType == 'Reinsurance' && acisPortfolioCashflow.length > 0" class="font-weight-bold">
        <td colspan="2">Totals</td>
        <td><!-- Deal Name --></td>
        <td><!-- Bond Name --></td>

        <!-- Initial Limit / Balance -->
        <td class="text-right">{{ totalOwnedFaceAmt }}</td>

        <!-- Current Limit / Current Balance -->
        <td>{{ currentBalanceTotal }}</td>
        <td><!-- Collateral / Price --></td>
        <td><!-- CPR --></td>
        <td><!-- CDR --></td>
        <td><!-- SEV --></td>
        <!-- DLQ -->
        <td class="text-right">ACIS</td>
        <!-- Scenario -->
        <td></td>
        <!-- Call -->
        <td></td>
        <!-- ROI / Yield -->
        <td class="text-right">{{ totalAcisYield }}</td>
        <!-- Rate On Line / WAL -->
        <td class="text-right">{{ totalAcisRateOnLine }}</td>
        <!-- Loss On Line / Write down -->
        <td class="text-right">{{ totalAcisLossOnLine }}</td>
        <!--Margin On Line / Spread -->
        <td class="text-right">{{ totalAcisReturnOnLine }}</td>
      </tr>
      <tr v-if="portfolioType == 'Reinsurance' && cirtPortfolioCashflow.length > 0" class="font-weight-bold">
        <td v-if="acisPortfolioCashflow.length == 0" colspan="2">Totals</td>
        <td v-else colspan="2"></td>
        <td><!-- Deal Name --></td>
        <td><!-- Bond Name --></td>

        <!-- Initial Limit / Balance -->
        <td v-if="acisPortfolioCashflow.length == 0" class="text-right">{{ totalOwnedFaceAmt }}</td>
        <td v-else></td>

        <!-- Current Limit / Current Balance -->
        <td v-if="acisPortfolioCashflow.length == 0" class="text-right"> {{ currentBalanceTotal }}</td>
        <td v-else></td>

        <td><!-- Collateral / Price --></td>
        <td><!-- CPR --></td>
        <td><!-- CDR --></td>
        <td><!-- SEV --></td>
        <!-- DLQ -->
        <td class="text-right">CIRT</td>
        <!-- Scenario -->
        <td></td>
        <!-- Call -->
        <td></td>
        <!-- ROI / Yield -->
        <td class="text-right">{{ totalCirtYield }}</td>
        <!-- Rate On Line / WAL -->
        <td class="text-right">{{ totalCirtRateOnLine }}</td>
        <!-- Loss On Line / Write down -->
        <td class="text-right">{{ totalCirtLossOnLine }}</td>
        <!--Margin On Line / Spread -->
        <td class="text-right">{{ totalCirtReturnOnLine }}</td>
      </tr>
      <tr v-if="portfolioType == 'Bonds' && bondPortfolioCashflow.length > 0" class="font-weight-bold">
        <td v-if="acisPortfolioCashflow.length == 0 && cirtPortfolioCashflow.length == 0" colspan="2">Totals</td>
        <td v-else colspan="2"></td>
        <td><!-- Deal Name --></td>
        <td><!-- Bond Name --></td>
        <td><!-- Initial Limit / Balance -->{{ totalOwnedFaceAtm }}</td>
        <!-- Current Limit / Current Balance -->
        <td>{{ currentBalanceTotal }}</td>
        <td><!-- Collateral / Price --></td>
        <td><!-- CPR --></td>
        <td><!-- CDR --></td>
        <td><!-- SEV --></td>
        <!-- DLQ -->
        <td class="text-right">Bonds</td>
        <!-- Scenario -->
        <td></td>
        <!-- Call -->
        <td></td>
        <!-- ROI / Yield -->
        <td class="text-right">{{ totalBondYield }}</td>
        <!-- Rate On Line / WAL -->
        <td class="text-right">{{ totalBondRateOnLine }}</td>
        <!-- Loss On Line / Write down -->
        <td class="text-right">{{ totalBondLossOnLine }}</td>
        <!--Margin On Line / Spread -->
        <td class="text-right">{{ totalBondReturnOnLine }}</td>
      </tr>
      </tbody>
    </table>
    </div>
    <b-tabs v-if="acisPortfolioCashflow.length + cirtPortfolioCashflow.length + bondPortfolioCashflow.length > 0">
      <b-tab title="ACIS" v-if="portfolioType == 'Reinsurance' && acisPortfolioCashflow.length > 0" :active="acisPortfolioCashflow.length > 0">
        <acis-cashflow :isReinsurancePortfolio="true" :acisCashflow="acisPortfolioCashflow" :totals="acisTotals" dealName="" bondName="" isPortfolio/>
      </b-tab>
      <b-tab title="CIRT" v-if="portfolioType == 'Reinsurance' && cirtPortfolioCashflow.length > 0"
             :active="acisPortfolioCashflow.length == 0 && cirtPortfolioCashflow.length > 0">
        <cirt-cashflow :isReinsurancePortfolio="true" :cashflow="cirtPortfolioCashflow" :totals="cirtTotals" dealName="" isPortfolio/>
      </b-tab>
      <bond-cashflow :bond="bondPortfolioCashflow" :isPortfolio="true" v-if="bondPortfolioCashflow.length > 0"/>
    </b-tabs>
  </div>
</template>

<script>
import service                            from '../../services/HttpRequestService';
import {convertToPctArray, stringToArray} from '../../utilities/ArrayUtils';
import formatter                          from '../../utilities/Formatter';
import Datepicker                         from 'vue2-datepicker';
import moment                             from 'moment';
import AcisCashflow                       from '../acis/Cashflow';
import CirtCashflow                       from '../cirt/Cashflow';
import BondCashflow                       from '../dealAnalysis/BondCashflow';
import Alert                              from '../Alert';
import ManagePortfoliosModal              from './ManagePortfoliosModal';
import SaveAsModal                        from './SaveAsModal';
import PortfolioEntry                     from './PortfolioEntry';
import {createAcisValidationErrorMessage} from '../../validators/inputValidator';
import {loadValuesFromVector}             from '../../utilities/SimulationVectors';
import {TRACE_ERROR}                      from '../../constants';
import ExcelSupport                       from "@/utilities/ExcelSupport";
import * as XLSX                          from 'xlsx';

export default {
  data() {
    return {
      settlementDate:        new Date(),
      interestRate:          0.0,
      libor:                 0.0,
      deals:                 [],
      selectedPortfolio:     '',
      portfolios:            {},
      portfolioCashflow:     [],
      acisPortfolioCashflow: [],
      cirtPortfolioCashflow: [],
      bondPortfolioCashflow: [],
      showSpinner:           true,
      alert:                 {
        timestamp: 0,
        type:      '',
        msg:       ''
      },
      vectors:               new Map(),
      totalOwnedFaceAmt:     '',
      totalMargin:           '',
      totalAcisYield:        '',
      totalAcisWritedownPct: '',
      totalAcisFirstLossPd:  '',
      totalAcisRateOnLine:   '',
      totalAcisLossOnLine:   '',
      totalAcisReturnOnLine: '',
      totalCirtRateOnLine:   '',
      totalCirtLossOnLine:   '',
      totalCirtReturnOnLine: '',
      totalCirtYield:        '',
      totalCirtWritedownPct: '',
      totalCirtFirstLossPd:  '',
      totalBondYield:        '',
      // totalAcisWritedownPct: "",
      // totalAcisFirstLossPd: "",
      totalBondRateOnLine:   '',
      totalBondLossOnLine:   '',
      totalBondReturnOnLine: '',
      acisTotals:            {},
      cirtTotals:            {},
      bondTotals:            {},
      unsavedChanges:        false,
      earlyCall:             false,
      scenarioListByDeals:   {},
      scenarioListCommon:    [],
      scenarioListFlat:      []
    };
  },
  computed: {
    applicableDeals:   function () {
      let filter =
              this.portfolios[this.selectedPortfolio].PortfolioType == 'Reinsurance'
                  ? deal => deal.startsWith('ACIS') || deal.startsWith('CIRT')
                  : deal => !(deal.startsWith('AICS') || deal.startsWith('CIRT'));
      return this.deals.filter(filter);
    },
    cirtActivityMonth: function () {
      let settlementDate = moment(this.settlementDate);
      let activityMonth  = moment()
          .year(settlementDate.year())
          .month(settlementDate.month() - 2)
          .date(25);
      return activityMonth.format('MM/YYYY');
    },
    portfolioType:     function () {
      if (this.portfolios[this.selectedPortfolio]) {
        return this.portfolios[this.selectedPortfolio].PortfolioType;
      }
    },
    isEditable() {
      if (this.portfolios.hasOwnProperty(this.selectedPortfolio)) {
        return this.portfolios[this.selectedPortfolio].IsEditable;
      }
      return false;
    },
    isNew() {
      if (this.portfolios.hasOwnProperty(this.selectedPortfolio)) {
        return this.portfolios[this.selectedPortfolio].IsNew;
      }
      return false;
    },
    isPortfolioConsistent() {
      return (
          this.selectedPortfolio &&
          this.portfolios &&
          this.portfolios[this.selectedPortfolio] &&
          this.portfolios[this.selectedPortfolio].PortfolioEntries &&
          this.portfolios[this.selectedPortfolio].PortfolioEntries.length > 0
      );
    },
    currentBalanceTotal() {
      if (this.isPortfolioConsistent && this.portfolios[this.selectedPortfolio].PortfolioEntries.length > 0) {
        return formatter.formatDollarValue(
            this.portfolios[this.selectedPortfolio].PortfolioEntries.reduce((acc, curr) => acc + curr.CurrentBalanceOriginal, 0)
        );
      }
      return null;
    },
    totalOwnedFaceAtm() {
      const total = this.portfolios[this.selectedPortfolio].PortfolioEntries
          .map(x => Number(x.OwnedFaceAmt.toString().replace(/,/g, '')))
          .reduce((x, y) => x + y, 0);
      return formatter.formatDollarValue(total);
    }
  },
  async created() {
    this.selectedPortfolio = this.$route.query.name || this.$cookies.get('portfolio') || this.$store.getters.portfolioName;
    this.$store.commit('changePortfolioName', this.selectedPortfolio);
    let res    = await service.getDealsNames();
    this.deals = res.data;
    res        = await service.getUserVectors();
    if (res.status == 200) {
      let vectors = res.data;
      for (let vector of vectors) {
        this.vectors.set(vector.Name, vector.Value.join(','));
      }
    }
    await this.loadPortfolios();
    setDataFromStore(this);
    this.$forceUpdate();

    if (this.$store.getters.portfolioResult.portfolioEntries.length) {
      this.portfolios[
          this.selectedPortfolio
          ].PortfolioEntries = this.$store.getters.portfolioResult.portfolioEntries;
    }
  },
  methods:    {
    portfolioEntriesPrepare() {
      if (this.portfolios.hasOwnProperty(this.selectedPortfolio)) {
        let entries = this.portfolios[this.selectedPortfolio].PortfolioEntries;
        for (let i = 0; i < entries.length; i++) {
          this.setAvailableScenarios(entries[i]);
        }
      }
    },
    // ##################################################
    // region Scenario related
    async loadPortfolioScenarios() {
      try {
        const pId = this.portfolios[this.selectedPortfolio].Id;
        let res   = await service.get('/portfolios/' + pId + '/scenarios');
        if (res.status == 200) {
          if (res.data[""]) {
            this.scenarioListCommon = res.data[""];
          }
          this.scenarioListFlat    = this.scenarioListCommon;
          this.scenarioListByDeals = res.data;
        }
      } catch (err) {
        await service.log(TRACE_ERROR, 'Error occurred get user scenarios', err.stack);
      }
    },
    getScenarioOptionName(scenario, dealName) {
      const specificTxt = scenario.DealName === dealName ? ' specific' : '';
      return `${scenario.ScenarioName} ${scenario.Vendor} ${specificTxt}`;
    },
    setAvailableScenarios(entry) {
      const scenarioListByDeals = this.scenarioListByDeals;
      let scenarioList          = [];
      if (scenarioListByDeals.hasOwnProperty(entry.DealName)) {
        const dealSpecificScenarios = scenarioListByDeals[entry.DealName];
        scenarioList                = scenarioList.concat(dealSpecificScenarios);
        this.scenarioListFlat       = this.scenarioListFlat.concat(dealSpecificScenarios);
      }
      scenarioList = scenarioList.concat(this.scenarioListCommon);
      scenarioList.forEach(element => {
        element.ScenarioNameWithVendor = this.getScenarioOptionName(element, entry.DealName);
      });
      const scenarioSelected = entry.hasOwnProperty('scenarioSelected')
          ? entry.scenarioSelected
          : null;
      this.$set(entry, 'scenarioSelected', scenarioSelected);
      this.$set(entry, 'scenarioList', scenarioList);
      return entry;
    },
    // endregion Scenario related
    // ##################################################
    setAvailableBondsForDeals(deals) {
      if (!(deals && deals.length > 0)) return;
      deals.forEach(deal => {
        if (deal) {
          const bonds = deal.data.Bonds;
          if (bonds && bonds.length > 0) {
            const portfolios       = this.portfolios[this.selectedPortfolio].PortfolioEntries;
            const portfoliosByDeal = portfolios.filter(p => p.DealName === deal.data.DealName);
            portfoliosByDeal.forEach(p => {
              this.$set(p, 'OwnedFaceAmt', formatter.formatDollarValue(p.OwnedFaceAmt));
              this.$set(p, 'AvailableBonds', deal.data.Bonds.map(bond => bond.BondName));
            });
          }
        }
      });
    },
    async updatePortfolio() {
      this.showSpinner = true;
      let entries      = [];
      let error        = validatePortfolio(this.portfolios[this.selectedPortfolio]);
      if (error !== '') {
        this.$refs.alert.showAlert('danger', error, 10);
        this.showSpinner = false;
        return false;
      }

      for (let entry of this.portfolios[this.selectedPortfolio].PortfolioEntries) {
        entries.push({
          DealName:      entry.DealName,
          BondName:      entry.BondName,
          OwnedFaceAmt:  entry.OwnedFaceAmt,
          Margin:        entry.Margin,
          Cpr:           stringToArray(loadValuesFromVector(entry.Cpr)),
          Cdr:           stringToArray(loadValuesFromVector(entry.Cdr)),
          LossSeverity:  stringToArray(loadValuesFromVector(entry.LossSeverity)),
          Delinquency:   stringToArray(loadValuesFromVector(entry.Delinquency)),
          Libor:         stringToArray(loadValuesFromVector(entry.Libor)),
          InterestRate:  entry.interestRate,
          OrdinalNumber: entry.OrdinalNumber
        });
      }
      try {
        let res = await service.updatePortfolio(this.portfolios[this.selectedPortfolio].Id, {
          PortfolioEntries: entries
        });
        this.$set(this.portfolios[this.selectedPortfolio], 'IsEditable', false);
        await this.loadPortfolioScenarios();
        this.portfolioEntriesPrepare();
        this.$refs.alert.showAlert('success', 'Succesfully updated portfolio.');
      } catch (err) {
        this.showSpinner = false;
        await service.log(TRACE_ERROR, 'Failed. ' + err.response.data.Message, err.stack);
        this.$refs.alert.showAlert('danger', 'Failed. ' + err.response.data.Message, 10);
        return false;
      }
      this.showSpinner = false;
      return true;
    },
    async toggleEditable() {
      if (!this.portfolios[this.selectedPortfolio]) {
        this.showModal(1);
      } else if (this.isEditable) {
        this.showSpinner = true;
        this.$set(this.portfolios[this.selectedPortfolio], 'IsEditable', false);
        try {
          let res                                 = await service.getPortfolioByName(this.selectedPortfolio);
          this.portfolios[this.selectedPortfolio] = res.data;
          for (let entry of this.portfolios[this.selectedPortfolio].PortfolioEntries) {
            if (entry.DealName !== '' && entry.DealName !== undefined) {
              let res = await service.get('deals/' + entry.DealName);
              this.$set(entry, 'AvailableBonds', res.data.Bonds.map(bond => bond.BondName));
            }
          }
          this.portfolioEntriesPrepare();
        } catch (err) {
          await service.log(TRACE_ERROR, err.response.data.Message, err.stack);
          this.$refs.alert.showAlert('danger', err.response.data.Message, 10);
        }
        this.showSpinner = false;
      } else {
        this.$set(this.portfolios[this.selectedPortfolio], 'IsEditable', true);
      }
    },
    async onDealNameChange(entry, newDealName) {
      if (newDealName != undefined) {
        this.showSpinner = true;
        try {
          let res = await service.get('deals/' + newDealName);
          this.$set(entry, 'AvailableBonds', res.data.Bonds.map(bond => bond.BondName));
          entry.BondName         = entry.AvailableBonds[0];
          entry.CancellationDate = res.data.OptionalCancellationDate;
          this.portfolioChanged();
        } catch (err) {
          const errMessage = 'Failed to load deal ' + newDealName + '.';
          await service.log(TRACE_ERROR, errMessage, err.stack);
          throw errMessage;
        } finally {
          this.showSpinner = false;
        }
      }
    },
    async changeSelectedPortfolio(newPortfolio) {
      this.showSpinner = true;

      await new Promise(resolve =>
          setTimeout(() => {
            clearResults(this);
            this.portfolioCashflow = [];
            this.selectedPortfolio = newPortfolio;
            this.$store.commit('changePortfolioName', this.selectedPortfolio);
            this.$cookies.set('portfolio', this.selectedPortfolio);
            resolve();
          }, 1000)
      );

      let promises        = [];
      let portfolioUnique = this.portfolios[this.selectedPortfolio].PortfolioEntries;
      portfolioUnique     = portfolioUnique.filter(
          (thing, index, self) => index === self.findIndex(t => t.DealName === thing.DealName)
      );

      for (let entry of portfolioUnique) {
        if (entry.DealName !== '' && entry.DealName !== undefined) {
          promises.push(service.get('deals/' + entry.DealName));
        }
      }
      await this.loadPortfolioScenarios();
      this.portfolioEntriesPrepare();
      let resp = await Promise.all(promises);
      this.setAvailableBondsForDeals(resp);
      this.showSpinner = false;
    },
    async runPortfolio() {
      this.acisPortfolioCashflow = [];
      this.cirtPortfolioCashflow = [];
      this.showSpinner           = true;
      let requestBodyEntries     = [];
      let hasErrorOccurred       = false;
      let errors                 = [];
      for (let i = 0; i < this.portfolios[this.selectedPortfolio].PortfolioEntries.length; i++) {
        let entry          = this.portfolios[this.selectedPortfolio].PortfolioEntries[i];
        let body           = {};
        let ownedFaceAmt   = Number(entry.OwnedFaceAmt.toString().replace(/,/g, ''));
        let settlementDate = moment(this.settlementDate);
        if (entry.DealName != '') {
          // ##########
          body.SettlementDate   = settlementDate.format('YYYYMMDD');
          body.DealName         = entry.DealName;
          body.BondName         = entry.BondName || null;
          body.OwnedFaceAmt     = ownedFaceAmt;
          body.Margin           = entry.Margin / 100 || null;
          body.InterestRate     = this.interestRate / 100 || 0;
          body.Libor            = convertToPctArray(this.libor);
          body.Price            = entry.Margin;
          body.Balance          = ownedFaceAmt;
          body.Cancellation     = entry.CleanupCall;
          body.CancellationDate = moment(entry.cancellationDate).format('YYYYMMDD') || null;
          body.CleanupCall      = entry.CleanupCall;
          // ##########
          // region Scenario
          const isScenarioSelected = Boolean(entry.scenarioSelected);
          if (isScenarioSelected) {
            const scenarioSelectedId = entry.scenarioSelected;
            const scenario           = this.scenarioListFlat.find(e => e.Id === scenarioSelectedId);
            if (scenario.hasOwnProperty('CollateralCashflow')) {
              Object.assign(body, {
                UserCollateralCashflow: scenario.CollateralCashflow
              });
            } else {
              Object.assign(body, {
                Cpr:          convertToPctArray(scenario.Cpr),
                Cdr:          convertToPctArray(scenario.Cdr),
                LossSeverity: convertToPctArray(scenario.LossSeverity),
                Delinquency:  convertToPctArray(scenario.Delinquency),
                Libor:        convertToPctArray(this.libor)
              });
            }
          } else {
            // If SCENARIO is NOT selected
            let cpr          = loadValuesFromVector(entry.Cpr);
            let cdr          = loadValuesFromVector(entry.Cdr);
            let lossSeverity = loadValuesFromVector(entry.LossSeverity);
            let delinquency  = loadValuesFromVector(entry.Delinquency);

            body.Cpr          = convertToPctArray(cpr);
            body.Cdr          = convertToPctArray(cdr);
            body.LossSeverity = convertToPctArray(lossSeverity);
            body.Delinquency  = convertToPctArray(delinquency);
          }
          // endregion Scenario
          // #####
          requestBodyEntries.push(body);
        } else {
          hasErrorOccurred = true;
          this.showSpinner = false;
          let emptyDealMsg = entry.DealName === '' ? ', Deal Name' : '';
          errors.push(
              'Line ' +
              (i + 1) +
              ': ' +
              createAcisValidationErrorMessage(
                  entry.Cpr,
                  entry.Cdr,
                  entry.LossSeverity,
                  entry.Delinquency,
                  entry.Libor,
                  ownedFaceAmt,
                  entry.Margin
              ) +
              emptyDealMsg
          );
          this.$refs.alert.showAlert('danger', errors.join('<br>'));
        }
      }
      if (!hasErrorOccurred) {
        let yieldErr = '';
        try {
          let body               = {
            Entries: requestBodyEntries
          };
          let res                = await service.runPortfolio(body);
          this.totalOwnedFaceAmt = formatter.formatDollarValue(res.data.TotalOwnedFaceAmt);
          setTotals(this, res.data.AcisPortfolio, res.data.CirtPortfolio, res.data.BondPortfolio);

          let acisEntryIndex = 0;
          let cirtEntryIndex = 0;
          let bondEntryIndex = 0;

          for (
              let i = 0;
              i < this.portfolios[this.selectedPortfolio].PortfolioEntries.length;
              i++
          ) {
            let portfolioEntry = this.portfolios[this.selectedPortfolio].PortfolioEntries[i];
            let entry          = portfolioEntry.DealName.startsWith('ACIS')
                ? res.data.AcisPortfolio.PortfolioEntries[acisEntryIndex++]
                : portfolioEntry.DealName.startsWith('CIRT')
                    ? res.data.CirtPortfolio.PortfolioEntries[cirtEntryIndex++]
                    : res.data.BondPortfolio.PortfolioEntries[bondEntryIndex++];

            let value = '-----';
            if (entry.Yield.IsSuccessful) {
              value = formatter.formatPctValue(entry.Yield.Value, 2);
            } else {
              // TODO sk: remove after redesigning c++ message system
              if (
                  entry.Yield.ErrorMessage !== "Can't compute Yield." &&
                  entry.Yield.ErrorMessage !== 'Too many tries in Yield calculation.' &&
                  entry.Yield.ErrorMessage !== 'Guess is too negative in Yield calculation.' &&
                  entry.Yield.ErrorMessage !== "Can't compute Spread." &&
                  entry.Yield.ErrorMessage !== 'Too many tries in Spread calculation.'
              ) {
                yieldErr += i + 1 + ': ' + entry.Yield.ErrorMessage + '<br>';
              }
            }

            this.$set(this.portfolios[this.selectedPortfolio].PortfolioEntries[i], 'Yield', value);
            if (entry.DealName.startsWith('ACIS') || entry.DealName.startsWith('CIRT')) {
              this.$set(
                  this.portfolios[this.selectedPortfolio].PortfolioEntries[i],
                  'RateOnLine',
                  formatter.formatPctValue(entry.LineMetrics.RateOnLine, 2)
              );
              this.$set(
                  this.portfolios[this.selectedPortfolio].PortfolioEntries[i],
                  'LossOnLine',
                  formatter.formatPctValue(entry.LineMetrics.LossOnLine, 2)
              );
              this.$set(
                  this.portfolios[this.selectedPortfolio].PortfolioEntries[i],
                  'ReturnOnLine',
                  formatter.formatPctValue(entry.LineMetrics.ReturnOnLine, 2)
              );
              this.$set(
                  this.portfolios[this.selectedPortfolio].PortfolioEntries[i],
                  'CurrentBalance',
                  entry.CurrentBalance ? formatter.formatDollarValue(entry.CurrentBalance) : '-----'
              );
            } else {
              let spread = '-----';
              if (entry.Spread.IsSuccessful) {
                spread = formatter.round(entry.Spread.Value, 2);
              } else {
                // TODO sk: remove after redesigning c++ message system
                if (
                    entry.Yield.ErrorMessage !== "Can't compute Yield." &&
                    entry.Yield.ErrorMessage !== 'Too many tries in Yield calculation.' &&
                    entry.Yield.ErrorMessage !== 'Guess is too negative in Yield calculation.' &&
                    entry.Yield.ErrorMessage !== "Can't compute Spread." &&
                    entry.Yield.ErrorMessage !== 'Too many tries in Spread calculation.'
                ) {
                  yieldErr += i + 1 + ': ' + entry.Yield.ErrorMessage + '<br>';
                }
              }
              this.$set(
                  this.portfolios[this.selectedPortfolio].PortfolioEntries[i],
                  'RateOnLine',
                  entry.Characteristics.Wal ? formatter.round(entry.Characteristics.Wal, 2) : '-----'
              );
              this.$set(
                  this.portfolios[this.selectedPortfolio].PortfolioEntries[i],
                  'LossOnLine',
                  entry.Characteristics.WritedownPct != null
                      ? formatter.formatPctValue(entry.Characteristics.WritedownPct, 2)
                      : '-----'
              );
              this.$set(
                  this.portfolios[this.selectedPortfolio].PortfolioEntries[i],
                  'ReturnOnLine',
                  spread
              );
              this.$set(
                  this.portfolios[this.selectedPortfolio].PortfolioEntries[i],
                  'CurrentBalance',
                  entry.CurrentBalance ? formatter.formatDollarValue(entry.CurrentBalance) : '-----'
              );
            }

            this.$set(this.portfolios[this.selectedPortfolio].PortfolioEntries[i], 'CurrentBalanceOriginal', entry.CurrentBalance);
          }

          if (res.data.AcisPortfolio) {
            this.acisPortfolioCashflow = formatter.formatDataInArray(
                res.data.AcisPortfolio.PortfolioCashflow,
                [
                  'RemainingLiability',
                  'ReductionInLiability',
                  'InsurerPaidLosses',
                  'InsurancePremium',
                  'CollateralTrustAccount',
                  'CollateralAccountChange',
                  'CollateralAccountInterest',
                  'NetInsuranceCashflow',
                  'CollateralBalance',
                  'CollateralLosses'
                ],
                ['PaymentDate'],
                ['CprRate', 'CdrRate', 'LossSeverityRate', 'DelinquencyRate']
            );
            this.acisTotals            = {
              ReductionInLiability: formatter.formatDollarValue(
                  res.data.AcisPortfolio.PortfolioCashflowTotals.ReductionInLiability
              ),
              InsurerPaidLosses:    formatter.formatDollarValue(
                  res.data.AcisPortfolio.PortfolioCashflowTotals.InsurerPaidLosses
              ),
              InsurancePremium:     formatter.formatDollarValue(
                  res.data.AcisPortfolio.PortfolioCashflowTotals.InsurancePremium
              ),
              CollateralLosses:     formatter.formatDollarValue(
                  res.data.AcisPortfolio.PortfolioCashflowTotals.CollateralLosses
              ),
              NetInsuranceCashflow: formatter.formatDollarValue(
                  res.data.AcisPortfolio.PortfolioCashflowTotals.NetInsuranceCashflow
              )
            };
          }

          if (res.data.CirtPortfolio) {
            this.cirtPortfolioCashflow = formatter.formatDataInArray(
                res.data.CirtPortfolio.PortfolioCashflow,
                [
                  'CollateralBalance',
                  'CollateralLosses',
                  'RemainingLiability',
                  'ReductionInLiability',
                  'InsurerPaidLosses',
                  'InsurancePremium',
                  'CollateralTrustAccount',
                  'CollateralAccountChange',
                  'CollateralAccountInterest',
                  'NetInsuranceCashflow'
                ],
                [],
                ['CprRate', 'CdrRate', 'LossSeverityRate', 'DelinquencyRate']
            );

            for (var row of this.cirtPortfolioCashflow) {
              row['PaymentDate'] = formatter.formatDateValue(row['PaymentDate'], 'MM/YYYY');
            }

            this.cirtTotals = {
              CollateralLosses:     formatter.formatDollarValue(
                  res.data.CirtPortfolio.PortfolioCashflowTotals.CollateralLosses
              ),
              ReductionInLiability: formatter.formatDollarValue(
                  res.data.CirtPortfolio.PortfolioCashflowTotals.ReductionInLiability
              ),
              InsurerPaidLosses:    formatter.formatDollarValue(
                  res.data.CirtPortfolio.PortfolioCashflowTotals.InsurerPaidLosses
              ),
              InsurancePremium:     formatter.formatDollarValue(
                  res.data.CirtPortfolio.PortfolioCashflowTotals.InsurancePremium
              ),
              NetInsuranceCashflow: formatter.formatDollarValue(
                  res.data.CirtPortfolio.PortfolioCashflowTotals.NetInsuranceCashflow
              )
            };
          }

          if (res.data.BondPortfolio) {
            this.bondPortfolioCashflow = formatter.formatDataInArray(
                res.data.BondPortfolio.PortfolioCashflow,
                ['Balance', 'Principal', 'ActualWriteDown', 'Interest', 'Cash'],
                ['PaymentDate'],
                ['CprRate', 'CdrRate', 'LossSeverityRate', 'DelinquencyRate', 'Rate']
            );
            for (let i = 0; i < this.bondPortfolioCashflow.length; i++) {
              if (i > 0 && this.bondPortfolioCashflow[i - 1]['Balance'] < 0.005) {
                this.bondPortfolioCashflow = this.bondPortfolioCashflow.slice(0, i);
                break;
              }
            }
          }
          this.$store.commit('savePortfolioResult', {
            portfolioEntries:      this.portfolios[this.selectedPortfolio].PortfolioEntries,
            totals:                {
              OwnedFaceAmt: this.totalOwnedFaceAmt,
              AcisYield:    this.totalAcisYield,
              AcisRol:      this.totalAcisRateOnLine,
              AcisLol:      this.totalAcisLossOnLine,
              AcisRetol:    this.totalAcisReturnOnLine,
              CirtYield:    this.totalCirtYield,
              CirtRol:      this.totalCirtRateOnLine,
              CirtLol:      this.totalCirtLossOnLine,
              CirtRetol:    this.totalCirtReturnOnLine
            },
            acisPortfolioCashflow: this.acisPortfolioCashflow,
            cirtPortfolioCashflow: this.cirtPortfolioCashflow,
            cirtTotals:            this.cirtTotals,
            acisTotals:            this.acisTotals,
            interestRate:          this.interestRate,
            libor:                 this.libor
          });
          this.$forceUpdate();
        } catch (err) {
          await service.log(TRACE_ERROR, err.response.data.Message, err.stack);
          this.$refs.alert.showAlert('danger', err.response.data.Message.split(',').join('<br>'));
        }
        if (yieldErr.length > 0) {
          this.$refs.alert.showAlert('danger', [...new Set(yieldErr.split('<br>'))].join('<br>'));
        }
      }
      this.showSpinner = false;
    },
    addPortfolioEntry() {
      clearResults(this);
      let length = this.portfolios[this.selectedPortfolio].PortfolioEntries.length + 1;

      let marign = this.portfolioType == 'Reinsurance' ? '20' : '100';

      this.portfolios[this.selectedPortfolio].PortfolioEntries.push({
        DealName:       '',
        BondName:       '',
        OwnedFaceAmt:   '1,000,000.00',
        Margin:         marign,
        Cpr:            '0',
        Cdr:            '0',
        LossSeverity:   '0',
        Delinquency:    '0',
        Libor:          '0',
        InterestRate:   '0',
        AvailableBonds: [],
        OrdinalNumber:  length
      });
      this.portfolioChanged();
      return this.portfolios[this.selectedPortfolio].PortfolioEntries[length - 1];
    },
    showModal(num) {
      switch (num) {
        case 1:
          this.$refs.managePortfoliosModal.showModal();
          break;
        case 2:
          this.$refs.saveAsModal.showModal();
          break;
      }
    },
    removeEntry(index) {
      this.portfolios[this.selectedPortfolio].PortfolioEntries.splice(index, 1);
      for (let i = 0; i < this.portfolios[this.selectedPortfolio].PortfolioEntries.length; i++) {
        this.portfolios[this.selectedPortfolio].PortfolioEntries[i].OrdinalNumber = i + 1;
      }
      this.portfolioChanged();
    },
    async save() {
      clearResults(this);
      let shouldSave       = true;
      let errMsg           = '';
      let portfoliosToSave = [];
      this.showSpinner     = true;
      for (let portfolioName of Object.keys(this.portfolios)) {
        let portfolio = this.portfolios[portfolioName];
        if (portfolio.hasOwnProperty('IsNew')) {
          portfoliosToSave.push(portfolio);
          let error = validatePortfolio(portfolio);
          if (error !== '') {
            shouldSave = false;
            if (portfolio.Name.length > 0) {
              errMsg += portfolio.Name + ':<br>' + error;
            }
            continue;
          }
        }
      }
      if (shouldSave) {
        let requestBodyEntries = [];
        for (let portfolio of portfoliosToSave) {
          let portfolioReq = {
            Name:             portfolio.Name,
            PortfolioType:    portfolio.PortfolioType,
            PortfolioEntries: []
          };
          for (let entry of portfolio.PortfolioEntries) {
            portfolioReq.PortfolioEntries.push({
              DealName:      entry.DealName,
              BondName:      entry.BondName,
              OwnedFaceAmt:  entry.OwnedFaceAmt,
              Margin:        entry.Margin,
              Cpr:           stringToArray(loadValuesFromVector(entry.Cpr)),
              Cdr:           stringToArray(loadValuesFromVector(entry.Cdr)),
              LossSeverity:  stringToArray(loadValuesFromVector(entry.LossSeverity)),
              Delinquency:   stringToArray(loadValuesFromVector(entry.Delinquency)),
              Libor:         stringToArray(loadValuesFromVector(entry.Libor)),
              InterestRate:  entry.interestRate,
              OrdinalNumber: entry.OrdinalNumber
            });
          }
          requestBodyEntries.push(portfolioReq);
        }

        try {
          await service.savePortfolios(requestBodyEntries);
          this.unsavedChanges = false;
          await this.reload();
          this.$refs.alert.showAlert('success', 'Saved portfolios in the database.');
        } catch (err) {
          const errMessage =
                    'Failed to save portfolios in the database.' + err.response.data.Message;
          await service.log(TRACE_ERROR, errMessage, err.stack);
          this.$refs.alert.showAlert('danger', errMessage, 10);
          this.showSpinner = false;
          return false;
        }
      } else {
        this.$refs.alert.showAlert('danger', errMsg, 4 * errMsg.split('<br>').length);
      }
      this.showSpinner = false;
      return true;
    },
    async saveAs(newPortfolioName) {
      clearResults(this);
      this.showSpinner     = true;
      let currentPortfolio = this.portfolios[this.selectedPortfolio];
      let entries          = [];
      for (let entry of currentPortfolio.PortfolioEntries) {
        entries.push({
          DealName:       entry.DealName,
          BondName:       entry.BondName,
          OwnedFaceAmt:   entry.OwnedFaceAmt,
          Margin:         entry.Margin,
          Cpr:            stringToArray(loadValuesFromVector(entry.Cpr)),
          Cdr:            stringToArray(loadValuesFromVector(entry.Cdr)),
          LossSeverity:   stringToArray(loadValuesFromVector(entry.LossSeverity)),
          Delinquency:    stringToArray(loadValuesFromVector(entry.Delinquency)),
          Libor:          stringToArray(loadValuesFromVector(entry.Libor)),
          InterestRate:   entry.interestRate,
          OrdinalNumber:  entry.OrdinalNumber,
          AvailableBonds: entry.AvailableBonds
        });
      }

      const owner                       = this.$store.getters.username;
      this.portfolios[newPortfolioName] = {
        Owner:            owner,
        Name:             newPortfolioName,
        PortfolioType:    currentPortfolio.PortfolioType,
        PortfolioEntries: entries
      };

      this.portfolios        = {...this.portfolios};
      this.selectedPortfolio = newPortfolioName;
      try {
        let res = await service.addPortfolio(this.portfolios[newPortfolioName]);
        res     = await service.getPortfolioByName(newPortfolioName);
        this.$set(this.portfolios[newPortfolioName], 'Id', res.data.Id);
        this.$refs.alert.showAlert(
            'success',
            'Successfully save the ' + newPortfolioName + ' portfolio.'
        );
      } catch (err) {
        await service.log(TRACE_ERROR, err.response.data.Message, err.stack);
        this.$refs.alert.showAlert('danger', err.response.data.Message, 10);
      }
      this.showSpinner = false;
    },
    async reload() {
      await this.loadPortfolios();
      this.unsavedChanges = false;
    },
    onPortfolioAdded(updated, newPortfolioName) {
      this.portfolios                          = Object.assign({}, updated);
      this.portfolioCashflow[newPortfolioName] = [];
      this.selectedPortfolio                   = newPortfolioName;
      this.unsavedChanges                      = true;
      this.addPortfolioEntry();
      this.portfolioEntriesPrepare();
    },
    // LOADED
    async onPortfolioLoaded(data) {
      let err          = new Set();
      let dealNamesRes = await service.getDealsNames();
      let dealNames    = dealNamesRes.data;

      if (data[0].length != 9) {
        err.add(
            'Incorrect file format. Required file headres are:<br>' +
            '1. Reinsurance Portfolio:<br>Portfolio Name, Deal id, Bond id, Initial Limit ($), Collateral Hold (%), CPR (%), CDR (%), Sev (%), Del (%)<br>' +
            '2. Bonds Portfolio:<br>Portfolio Name, Deal id, Bond id, Balance ($), Price ($), CPR (%), CDR (%), Sev (%), Del (%)<br>'
        );
      }
      if (err.size == 0) {
        for (let i = 1; i < data.length && data[i].length > 0; i++) {
          let portfolioName   = data[i][0];
          let portfolioExists = this.portfolios.hasOwnProperty(portfolioName);
          let dealName        = decodeURI(data[i][1]);

          if (!portfolioExists) {
            this.portfolios[portfolioName]        = {
              Name:             portfolioName,
              PortfolioType:
                                dealName.startsWith('ACIS') || dealName.startsWith('CIRT')
                                    ? 'Reinsurance'
                                    : 'Bonds',
              PortfolioEntries: [],
              IsNew:            true,
              IsEditable:       true
            };
            this.portfolios                       = Object.assign({}, this.portfolios);
            this.portfolioCashflow[portfolioName] = [];
          } else if (!this.portfolios[portfolioName].hasOwnProperty('IsNew')) {
            err.add(portfolioName + ' portfolio already exists.');
            continue;
          }

          this.selectedPortfolio = portfolioName;

          if (!dealNames.includes(dealName)) {
            err.add('Portfolio ' + portfolioName + ': ' + dealName + " deal doesn't exist.");
            continue;
          }

          if (
              (dealName.startsWith('ACIS') || dealName.startsWith('CIRT')) &&
              this.portfolios[portfolioName].PortfolioType == 'Bonds'
          ) {
            err.add(
                'Portfolio ' +
                portfolioName +
                ': ' +
                dealName +
                " deal can't be added to Bonds portfolio."
            );
            continue;
          } else if (
              !(dealName.startsWith('ACIS') || dealName.startsWith('CIRT')) &&
              this.portfolios[portfolioName].PortfolioType == 'Reinsurance'
          ) {
            err.add(
                'Portfolio ' +
                portfolioName +
                ': ' +
                dealName +
                " deal can't be added to Reinsurance portfolio."
            );
            continue;
          }

          let entry      = this.addPortfolioEntry();
          entry.DealName = dealName;

          try {
            await this.onDealNameChange(entry, entry.DealName);
            let bondName   = data[i][2] || 'A';
            let bondExists = entry.AvailableBonds.find(e => e == bondName);
            if (bondExists) {
              entry.BondName = bondName;
            } else {
              err.add(
                  'Portfolio ' +
                  portfolioName +
                  ': ' +
                  bondName +
                  " bond doesn't exist in " +
                  dealName +
                  ' deal.'
              );
              this.removeEntry(this.portfolios[portfolioName].PortfolioEntries.length - 1);
              continue;
            }
            entry.OwnedFaceAmt = formatter.formatDollarValue(data[i][3] || 0);
            entry.Margin       =
                this.portfolios[portfolioName].PortfolioType == 'Bonds'
                    ? formatter.formatDollarValue(data[i][4] || 0)
                    : data[i][4] * 100 || 0;
            entry.Cpr          = data[i][5] * 100 || 0;
            entry.Cdr          = data[i][6] * 100 || 0;
            entry.LossSeverity = data[i][7] * 100 || 0;
            entry.Delinquency  = data[i][8] * 100 || 0;

            this.portfolios     = Object.assign({}, this.portfolios);
            this.unsavedChanges = true;
          } catch (err) {
            await this.reload();
            await service.log(TRACE_ERROR, 'Please review the input XLSX file', err.stack);
            this.$refs.alert.showAlert('danger', err + ' Please review the input XLSX file.');
            return;
          }
        }
        this.portfolioEntriesPrepare();
      }
      if (err.size != 0) {
        this.$refs.alert.showAlert('danger', Array.from(err).join('<br>'), err.size * 10);
      }
    },
    async onPortfolioDeleted(indexesToRemove) {
      let portfoliosToRemove = indexesToRemove.map(index => this.portfolios[index].Id);
      try {
        let res = await service.deletePortfolios(portfoliosToRemove);
        await this.reload();
        this.$refs.alert.showAlert('success', 'Successfully removed portfolios.');
      } catch (err) {
        await service.log(TRACE_ERROR, err.response.data.Message, err.stack);
        this.$refs.alert.showAlert('danger', err.response.data.Message, 10);
      }
    },
    onError(err) {
      this.$refs.alert.showAlert('danger', err.message);
    },
    async loadPortfolios() {
      this.showSpinner = true;
      try {
        let res = await service.getPortfolios();
        if (res.status == 200) {
          this.portfolios        = {};
          this.portfolios        = res.data;
          this.selectedPortfolio = this.portfolios.hasOwnProperty(this.selectedPortfolio)
              ? this.selectedPortfolio
              : this.portfolios[Object.keys(this.portfolios)[0]].Name;
          this.portfolioCashflow = [];

          let promises        = [];
          let portfolioUnique = this.portfolios[this.selectedPortfolio].PortfolioEntries;
          portfolioUnique     = portfolioUnique.filter(
              (thing, index, self) => index === self.findIndex(t => t.DealName === thing.DealName)
          );
          for (let entry of portfolioUnique) {
            promises.push(service.get('deals/' + entry.DealName + '?type=acis'));
          }
          let resp = await Promise.all(promises);
          this.setAvailableBondsForDeals(resp);
          await this.loadPortfolioScenarios();
          this.portfolioEntriesPrepare();
          this.$forceUpdate();
        } else {
          this.$refs.alert.showAlert('danger', 'No portfolios found.');
        }
      } catch (err) {
        await service.log(TRACE_ERROR, err.response.data.Message, err.stack);
        this.$refs.alert.showAlert('danger', err.response.data);
      }
      this.showSpinner = false;
    },
    portfolioChanged() {
      this.$set(this.portfolios[this.selectedPortfolio], 'IsUpdated', true);
    },
    onChildChanged(propVal, propName, index) {
      this.$set(this.portfolios[this.selectedPortfolio].PortfolioEntries[index], propName, propVal);
      // this.$forceUpdate();
    },
    exportPortfolioToExcel() {
      // #####
      const VAL_PORTFOLIO_NAME           = `${this.portfolios[this.selectedPortfolio].Name} EXPORTED`;
      const VAL_PORTFOLIO_TYPE           = `${this.portfolioType}`;
      const flagIsReinsurance            = VAL_PORTFOLIO_TYPE == 'Reinsurance';
      const TXT_PORTFOLIO_NAME           = "Portfolio Name";
      const TXT_DEAL_NAME                = "Deal Name";
      const TXT_BOND_NAME                = "Bond Name";
      const TXT_INITIAL_LIMIT_OR_BALANCE = flagIsReinsurance ? 'Contract Amount ($)' : 'Balance ($)'; //  Initial limit / Balance
      const TXT_COLLATERAL_OR_PRICE      = flagIsReinsurance ? 'Collateral Hold (%)' : 'Price ($)'; // Collateral / Price
      const TXT_CPR                      = "Base CPR (%)";
      const TXT_CDR                      = "Base CDR (%)";
      const TXT_SEV                      = "Base Sev (%)";
      const TXT_DEL                      = "Base Del (%)";
      // #####
      const fileName  = `PORTFOLIO ${VAL_PORTFOLIO_NAME} --- TYPE ${VAL_PORTFOLIO_TYPE}.xlsx`;
      const sheetName = `${VAL_PORTFOLIO_NAME}`.substring(0, 30);
      const fileData  = [];
      const sheetData = [];
      const headers   = [
        TXT_PORTFOLIO_NAME,
        TXT_DEAL_NAME,
        TXT_BOND_NAME,
        TXT_INITIAL_LIMIT_OR_BALANCE,
        TXT_COLLATERAL_OR_PRICE,
        TXT_CPR,
        TXT_CDR,
        TXT_SEV,
        TXT_DEL
      ];
      sheetData.push(headers);
      for (let entry of this.portfolios[this.selectedPortfolio].PortfolioEntries) {
        let VAL_MARGIN                        = flagIsReinsurance ? {v: entry.Margin / 100, z: '0.00%'} : entry.Margin;
        let VAL_CPR                           = {v: entry.Cpr[0] / 100, z: '0.00%'};
        let VAL_CDR                           = {v: entry.Cdr[0] / 100, z: '0.00%'};
        let VAL_SEV                           = {v: entry.LossSeverity[0] / 100, z: '0.00%'};
        let VAL_DEL                           = {v: entry.Delinquency[0] / 100, z: '0.00%'};
        const tempObj                         = {};
        tempObj[TXT_PORTFOLIO_NAME]           = VAL_PORTFOLIO_NAME;
        tempObj[TXT_DEAL_NAME]                = entry.DealName;
        tempObj[TXT_BOND_NAME]                = entry.BondName;
        tempObj[TXT_INITIAL_LIMIT_OR_BALANCE] = entry.OwnedFaceAmt; //  Initial limit / Balance
        tempObj[TXT_COLLATERAL_OR_PRICE]      = VAL_MARGIN; // Collateral / Price
        tempObj[TXT_CPR]                      = VAL_CPR;
        tempObj[TXT_CDR]                      = VAL_CDR;
        tempObj[TXT_SEV]                      = VAL_SEV;
        tempObj[TXT_DEL]                      = VAL_DEL;
        sheetData.push(Object.values(tempObj));
      } /*end for (let entry of this.portfolios[this.selectedPortfolio].PortfolioEntries) {*/
      // #####
      fileData.push({sheetName: sheetName, sheetData: sheetData});
      ExcelSupport.exportToXlsx(fileData, fileName);
      return this;
    },
  },
  components: {
    Datepicker,
    AcisCashflow,
    CirtCashflow,
    BondCashflow,
    Alert,
    ManagePortfoliosModal,
    PortfolioEntry,
    SaveAsModal
  },
  watch:      {
    earlyCall(newVal) {
      if (this.portfolios[this.selectedPortfolio]) {
        this.portfolios[this.selectedPortfolio].PortfolioEntries.map((item, index) => {
          this.onChildChanged(newVal, 'CleanupCall', index);
          this.onChildChanged(newVal, 'Cancellation', index);
        });
      }
    }
  },
  async beforeRouteLeave(to, from, next) {
    let proceedRouting = true;
    if (this.unsavedChanges) {
      const shouldSave = window.confirm('You have unsaved changes! Do you want to save them?');
      if (shouldSave) {
        proceedRouting = await this.save();
      }
    } else if (this.isEditable) {
      const shouldSave = window.confirm(
          'You have unsaved changes in the ' +
          this.selectedPortfolio +
          ' portfolio. Do you want to save them before leaving?'
      );
      if (shouldSave) {
        proceedRouting = await this.updatePortfolio();
      }
    }
    next(proceedRouting);
  }
};

function validatePortfolio(portfolio) {
  let error = '';

  for (let entry of portfolio.PortfolioEntries) {
    if (entry.DealName === '' || entry.DealName == undefined) {
      error += 'Line ' + entry.OrdinalNumber + ": Deal Name mustn't be empty<br>";
    } else if (entry.BondName === '' || entry.BondName == undefined) {
      error += 'Line ' + entry.OrdinalNumber + ": Bond Name mustn't be empty<br>";
    }
  }

  return error;
}

function setTotals(root, acis, cirt, bond) {
  if (acis != undefined) {
    root.totalAcisRateOnLine   = formatter.formatPctValue(acis.LineMetrics.RateOnLine, 2);
    root.totalAcisLossOnLine   = formatter.formatPctValue(acis.LineMetrics.LossOnLine, 2);
    root.totalAcisReturnOnLine = formatter.formatPctValue(acis.LineMetrics.ReturnOnLine, 2);

    root.totalAcisYield = '-----';
    if (acis.Yield.IsSuccessful) {
      root.totalAcisYield = formatter.formatPctValue(acis.Yield.Value, 2);
    } else {
      // TODO sk: remove after redesigning c++ message system
      if (
          acis.Yield.ErrorMessage !== "Can't compute Yield." &&
          acis.Yield.ErrorMessage !== 'Too many tries in Yield calculation.' &&
          acis.Yield.ErrorMessage !== 'Guess is too negative in Yield calculation.' &&
          acis.Yield.ErrorMessage !== "Can't compute Spread." &&
          acis.Yield.ErrorMessage !== 'Too many tries in Spread calculation.'
      ) {
        root.errorMessage = acis.Yield.ErrorMessage;
      }
    }
  }
  if (cirt != undefined) {
    root.totalCirtRateOnLine   = formatter.formatPctValue(cirt.LineMetrics.RateOnLine, 2);
    root.totalCirtLossOnLine   = formatter.formatPctValue(cirt.LineMetrics.LossOnLine, 2);
    root.totalCirtReturnOnLine = formatter.formatPctValue(cirt.LineMetrics.ReturnOnLine, 2);

    root.totalCirtYield = '-----';
    if (cirt.Yield.IsSuccessful) {
      root.totalCirtYield = formatter.formatPctValue(cirt.Yield.Value, 2);
    } else {
      // TODO sk: remove after redesigning c++ message system
      if (
          cirt.Yield.ErrorMessage !== "Can't compute Yield." &&
          cirt.Yield.ErrorMessage !== 'Too many tries in Yield calculation.' &&
          cirt.Yield.ErrorMessage !== 'Guess is too negative in Yield calculation.' &&
          cirt.Yield.ErrorMessage !== "Can't compute Spread." &&
          cirt.Yield.ErrorMessage !== 'Too many tries in Spread calculation.'
      ) {
        root.errorMessage = cirt.Yield.ErrorMessage;
      }
    }
  }
  if (bond != undefined) {
    root.totalBondRateOnLine = formatter.round(bond.TotalCharacteristics.Wal, 2);
    root.totalBondLossOnLine = formatter.formatPctValue(bond.TotalCharacteristics.WritedownPct, 2);

    root.totalBondYield = '-----';
    if (bond.Yield.IsSuccessful) {
      root.totalBondYield = formatter.formatPctValue(bond.Yield.Value, 2);
    } else {
      // TODO sk: remove after redesigning c++ message system
      if (
          bond.Yield.ErrorMessage !== "Can't compute Yield." &&
          bond.Yield.ErrorMessage !== 'Too many tries in Yield calculation.' &&
          bond.Yield.ErrorMessage !== 'Guess is too negative in Yield calculation.' &&
          bond.Yield.ErrorMessage !== "Can't compute Spread." &&
          bond.Yield.ErrorMessage !== 'Too many tries in Spread calculation.'
      ) {
        root.errorMessage = bond.Yield.ErrorMessage;
      }
    }

    root.totalBondReturnOnLine = '-----';
    if (bond.Spread.IsSuccessful) {
      root.totalBondReturnOnLine = formatter.round(bond.Spread.Value, 2);
    } else {
      // TODO sk: remove after redesigning c++ message system
      if (
          bond.Spread.ErrorMessage !== "Can't compute Yield." &&
          bond.Spread.ErrorMessage !== 'Too many tries in Yield calculation.' &&
          bond.Spread.ErrorMessage !== 'Guess is too negative in Yield calculation.' &&
          bond.Spread.ErrorMessage !== "Can't compute Spread." &&
          bond.Spread.ErrorMessage !== 'Too many tries in Spread calculation.'
      ) {
        root.errorMessage = bond.Spread.ErrorMessage;
      }
    }
  }
}

function clearResults(root) {
  root.$store.commit('savePortfolioResult', {
    portfolioEntries:      [],
    totals:                {
      OwnedFaceAmt: '',
      AcisYield:    '',
      AcisRol:      '',
      AcisLol:      '',
      AcisRetol:    '',
      CirtYield:    '',
      CirtRol:      '',
      CirtLol:      '',
      CirtRetol:    ''
    },
    acisPortfolioCashflow: [],
    cirtPortfolioCashflow: [],
    bondPortfolioCashflow: [],
    cirtTotals:            [],
    acisTotals:            [],
    bondTotals:            [],
    interestRate:          0.0,
    libor:                 0.0
  });
  root.acisPortfolioCashflow = [];
  root.cirtPortfolioCashflow = [];
  root.bondPortfolioCashflow = [];
  root.cirtTotals            = [];
  root.acisTotals            = [];
  root.bondTotals            = [];
  root.interestRate          = 0.0;
  root.libor                 = 0.0;
}

function setDataFromStore(root) {
  root.totalOwnedFaceAmt     = root.$store.getters.portfolioResult.totals.ownedFaceAmt;
  root.totalAcisYield        = root.$store.getters.portfolioResult.totals.acisYield;
  root.totalAcisRateOnLine   = root.$store.getters.portfolioResult.totals.acisRol;
  root.totalAcisLossOnLine   = root.$store.getters.portfolioResult.totals.acisLol;
  root.totalAcisReturnOnLine = root.$store.getters.portfolioResult.totals.acisRetol;
  root.totalCirtYield        = root.$store.getters.portfolioResult.totals.cirtYield;
  root.totalCirtRateOnLine   = root.$store.getters.portfolioResult.totals.cirtRol;
  root.totalCirtLossOnLine   = root.$store.getters.portfolioResult.totals.cirtLol;
  root.totalCirtReturnOnLine = root.$store.getters.portfolioResult.totals.cirtRetol;
  root.acisPortfolioCashflow = root.$store.getters.portfolioResult.acisPortfolioCashflow || [];
  root.cirtPortfolioCashflow = root.$store.getters.portfolioResult.cirtPortfolioCashflow || [];
  root.cirtTotals            = root.$store.getters.portfolioResult.cirtTotals;
  root.acisTotals            = root.$store.getters.portfolioResult.acisTotals;
  root.acisTotals            = root.$store.getters.portfolioResult.bondTotals;
  root.interestRate          = root.$store.getters.portfolioResult.interestRate || 0.0;
  root.libor                 = root.$store.getters.portfolioResult.libor || 0.0;
}
</script>

<style lang="scss" scoped>
.transformed {
  transform: translateY(-12px);
}
</style>

<style lang="scss">
.parameters-label-condensed {
  letter-spacing: -1px;
}

.w {
  &-5 {
    width: 5% !important;
  }

  &-6 {
    width: 6% !important;
  }
}
</style>
